import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogBody } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { apiClient, managedClusterBase, type ManagedClusterKind } from '../../lib/api-client';
import { Download, FileText, Loader2, Play, Search, Square, RotateCcw, TerminalSquare } from 'lucide-react';
import { LogSearchBar } from '../logs/LogSearchBar';
import { filterLogLinesWithContext, highlightMatches } from '../logs/logSearch';
import { InlineConfirmation } from '../ui/InlineConfirmation';
import { TerminalSessionDialog } from '../TerminalSessionDialog';

type PodRow = {
  name: string;
  namespace: string;
  phase: string;
  reason: string | null;
  ready: string;
  restarts: number;
  nodeName: string | null;
  createdAt: string | null;
  containers: Array<{ name: string; image: string }>;
};

type PodEvent = {
  type: string | null;
  reason: string | null;
  message: string | null;
  count: number;
  firstTimestamp: string | null;
  lastTimestamp: string | null;
};

export function LogsEventsDialog(props: {
  open: boolean;
  onClose: () => void;
  tenantCode: string;
  instanceName: string;
  cloudProvider: string;
  clusterKind?: ManagedClusterKind;
}) {
  const { open, onClose, tenantCode, instanceName, cloudProvider, clusterKind } = props;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [namespaces, setNamespaces] = useState<string[]>([]);
  const [namespaceQuery, setNamespaceQuery] = useState('');
  const [selectedNamespace, setSelectedNamespace] = useState('');

  const [pods, setPods] = useState<PodRow[]>([]);
  const [podQuery, setPodQuery] = useState('');
  const [selectedPod, setSelectedPod] = useState<string | null>(null);

  const [isLogDialogOpen, setIsLogDialogOpen] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [logLines, setLogLines] = useState<string[]>([]);
  const eventSourceRef = useRef<EventSource | null>(null);
  const logContainerRef = useRef<HTMLDivElement>(null);
  const [isLogPinnedToBottom, setIsLogPinnedToBottom] = useState(true);
  const pinnedRef = useRef(true);
  const [logSearchQuery, setLogSearchQuery] = useState('');
  const [logCtxBefore, setLogCtxBefore] = useState(0);
  const [logCtxAfter, setLogCtxAfter] = useState(0);
  const [logScrollThumb, setLogScrollThumb] = useState<{ topPct: number; heightPct: number; visible: boolean }>({
    topPct: 0,
    heightPct: 100,
    visible: false,
  });
  const isDraggingThumbRef = useRef(false);
  const lastPointerIdRef = useRef<number | null>(null);

  const [isEventsOpen, setIsEventsOpen] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [events, setEvents] = useState<PodEvent[]>([]);
  const [eventsQuery, setEventsQuery] = useState('');
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [isRestartingPod, setIsRestartingPod] = useState(false);
  const [termOpen, setTermOpen] = useState(false);
  const [termPath, setTermPath] = useState<string | null>(null);

  const apiUrlForSse = useMemo(() => {
    // VITE_API_URL is intentionally "" in prod to use relative paths (must NOT use ||).
    return import.meta.env.VITE_API_URL ?? 'http://localhost:3000';
  }, []);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    setNamespaces([]);
    setSelectedNamespace('');
    setNamespaceQuery('');
    setPods([]);
    setSelectedPod(null);
    setPodQuery('');
    void (async () => {
      try {
        const data = await apiClient.getOpenshiftNamespacesAll(tenantCode, instanceName, cloudProvider, clusterKind);
        const list = (data?.namespaces || []).map((x: any) => String(x)).filter(Boolean).sort();
        setNamespaces(list);
      } catch (e: any) {
        setError(e?.message || 'Failed to load namespaces');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, tenantCode, instanceName, cloudProvider, clusterKind]);

  useEffect(() => {
    if (!open) return;
    if (!selectedNamespace) return;
    setLoading(true);
    setError(null);
    setPods([]);
    setSelectedPod(null);
    setShowRestartConfirm(false);
    void (async () => {
      try {
        const data = await apiClient.getOpenshiftPods(tenantCode, instanceName, cloudProvider, selectedNamespace, clusterKind);
        setPods((data?.pods || []) as PodRow[]);
      } catch (e: any) {
        setError(e?.message || 'Failed to load pods');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, selectedNamespace, tenantCode, instanceName, cloudProvider, clusterKind]);

  useEffect(() => {
    if (String(logSearchQuery).trim()) return; // searching: don't auto-follow
    if (!isStreaming) return;
    const el = logContainerRef.current;
    if (!el) return;
    if (!pinnedRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [logLines, isStreaming, isLogPinnedToBottom, logSearchQuery]);

  useEffect(() => {
    const el = logContainerRef.current;
    if (!el) return;

    const recompute = () => {
      const scrollHeight = el.scrollHeight;
      const clientHeight = el.clientHeight;
      const scrollTop = el.scrollTop;
      const canScroll = scrollHeight > clientHeight + 1;
      if (!canScroll) {
        setLogScrollThumb({ topPct: 0, heightPct: 100, visible: false });
        pinnedRef.current = true;
        setIsLogPinnedToBottom(true);
        return;
      }
      const pinnedThresholdPx = 8;
      const pinned = scrollTop + clientHeight >= scrollHeight - pinnedThresholdPx;
      pinnedRef.current = pinned;
      setIsLogPinnedToBottom(pinned);

      const heightPct = Math.max(8, Math.min(100, (clientHeight / scrollHeight) * 100));
      const maxScrollTop = scrollHeight - clientHeight;
      const topPct = maxScrollTop > 0 ? (scrollTop / maxScrollTop) * (100 - heightPct) : 0;
      setLogScrollThumb({ topPct, heightPct, visible: true });
    };

    recompute();
    el.addEventListener('scroll', recompute, { passive: true });
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', recompute as any);
      ro.disconnect();
    };
  }, [isLogDialogOpen, isStreaming, logLines.length]);

  useEffect(() => {
    // Cleanup SSE per user action; never share streams.
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isLogDialogOpen) return;
    stopStream();
    setLogLines([]);
    setLogSearchQuery('');
    setLogCtxBefore(0);
    setLogCtxAfter(0);
  }, [isLogDialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredLogs = useMemo(
    () => filterLogLinesWithContext(logLines, logSearchQuery, logCtxBefore, logCtxAfter),
    [logLines, logSearchQuery, logCtxBefore, logCtxAfter],
  );
  const hasLogSearch = String(logSearchQuery).trim().length > 0;

  const filteredNamespaces = useMemo(() => {
    const q = namespaceQuery.trim().toLowerCase();
    if (!q) return namespaces;
    return namespaces.filter((ns) => ns.toLowerCase().includes(q));
  }, [namespaces, namespaceQuery]);

  const filteredPods = useMemo(() => {
    const q = podQuery.trim().toLowerCase();
    if (!q) return pods;
    return pods.filter((p) => String(p.name || '').toLowerCase().includes(q));
  }, [pods, podQuery]);

  const stopStream = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsStreaming(false);
  };

  const startStream = () => {
    if (!selectedNamespace || !selectedPod) return;
    stopStream();
    setIsStreaming(true);
    setLogLines([]);
    setIsLogPinnedToBottom(true);
    setLogSearchQuery('');
    setLogCtxBefore(0);
    setLogCtxAfter(0);
    setIsLogDialogOpen(true);

    const es = new EventSource(
      `${apiUrlForSse}${managedClusterBase(clusterKind)}/${tenantCode}/${instanceName}/${cloudProvider}/namespaces/${selectedNamespace}/pods/${selectedPod}/logs/stream`,
      { withCredentials: true },
    );

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'line') {
          setLogLines((prev) => [...prev, data.line].slice(-2000));
        } else if (data.type === 'error') {
          setLogLines((prev) => [...prev, `[ERROR] ${data.line}`].slice(-2000));
        } else if (data.type === 'close') {
          es.close();
          setIsStreaming(false);
        }
      } catch {
        // ignore malformed frames
      }
    };

    es.onerror = () => {
      es.close();
      setIsStreaming(false);
      setError('Log stream connection lost');
    };

    eventSourceRef.current = es;
  };

  const downloadLogs = async () => {
    if (!selectedNamespace || !selectedPod) return;
    try {
      const logs = await apiClient.getOpenshiftPodLogs(tenantCode, instanceName, cloudProvider, selectedNamespace, selectedPod, clusterKind);
      const blob = new Blob([logs], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tenantCode}-${instanceName}-${selectedNamespace}-${selectedPod}-logs.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message || 'Failed to download logs');
    }
  };

  const openEvents = async () => {
    if (!selectedNamespace || !selectedPod) return;
    setIsEventsOpen(true);
    setEventsLoading(true);
    setEvents([]);
    setEventsQuery('');
    try {
      const data = await apiClient.getOpenshiftPodEvents(tenantCode, instanceName, cloudProvider, selectedNamespace, selectedPod, clusterKind);
      setEvents((data?.events || []) as PodEvent[]);
    } catch (e: any) {
      setError(e?.message || 'Failed to load events');
    } finally {
      setEventsLoading(false);
    }
  };

  const filteredEvents = useMemo(() => {
    const q = eventsQuery.trim().toLowerCase();
    if (!q) return events;
    return events.filter((e) => {
      const hay = `${e.type || ''} ${e.reason || ''} ${e.message || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [events, eventsQuery]);

  const selectedPodRow = useMemo(
    () => pods.find((p) => p.name === selectedPod) || null,
    [pods, selectedPod],
  );

  const execContainer = useMemo(() => {
    const names = (selectedPodRow?.containers || []).map((c) => String(c.name || '').trim()).filter(Boolean);
    return names.find((n) => n !== 'istio-proxy' && n !== 'istio-init' && !n.startsWith('istio-')) || names[0] || '';
  }, [selectedPodRow]);

  const restartPod = async () => {
    if (!selectedNamespace || !selectedPod) return;
    setIsRestartingPod(true);
    try {
      await apiClient.deleteOpenshiftPod(
        tenantCode,
        instanceName,
        cloudProvider,
        selectedNamespace,
        selectedPod,
        clusterKind,
      );
      setShowRestartConfirm(false);
      setSelectedPod(null);
      const data = await apiClient.getOpenshiftPods(tenantCode, instanceName, cloudProvider, selectedNamespace, clusterKind);
      setPods((data?.pods || []) as PodRow[]);
    } catch (e: any) {
      setError(e?.message || 'Failed to restart pod');
    } finally {
      setIsRestartingPod(false);
    }
  };

  const openPodTerminal = () => {
    if (!selectedNamespace || !selectedPod) return;
    const containerQs = execContainer ? `?container=${encodeURIComponent(execContainer)}` : '';
    setTermPath(
      `${managedClusterBase(clusterKind)}/${tenantCode}/${instanceName}/${cloudProvider}/namespaces/${selectedNamespace}/pods/${selectedPod}/exec${containerQs}`,
    );
    setTermOpen(true);
  };

  const closeAll = () => {
    stopStream();
    setIsLogDialogOpen(false);
    setIsEventsOpen(false);
    setTermOpen(false);
    setTermPath(null);
    setError(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && closeAll()}>
        <DialogContent maxWidth="4xl">
          <DialogHeader>
            <DialogTitle>Manage cluster — {instanceName} ({tenantCode})</DialogTitle>
            <DialogDescription>
              Select a namespace and pod, then stream logs, view events, restart the pod, or open a terminal.
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-800 dark:text-red-200 mb-3 text-sm">
                {error}
              </div>
            )}

            <div className="mb-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Namespace
                  </label>
                  <div className="flex flex-col gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        value={namespaceQuery}
                        onChange={(e) => setNamespaceQuery(e.target.value)}
                        placeholder="Search namespaces..."
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none"
                      />
                    </div>

                    {/* Filtered namespace list (matches pod search behavior) */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <div className="max-h-56 overflow-y-auto">
                        {loading && namespaces.length === 0 ? (
                          <div className="space-y-2 p-3">
                            {[1, 2, 3].map((i) => (
                              <Skeleton key={i} className="h-8 w-full" />
                            ))}
                          </div>
                        ) : filteredNamespaces.length === 0 ? (
                          <div className="p-3 text-sm text-gray-500 dark:text-gray-400">
                            {namespaceQuery ? `No namespaces matching "${namespaceQuery}"` : 'No namespaces found'}
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredNamespaces.map((ns) => (
                              <button
                                key={ns}
                                type="button"
                                className={`w-full text-left px-3 py-2 text-sm font-mono hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                                  ns === selectedNamespace ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                                }`}
                                onClick={() => setSelectedNamespace(ns)}
                              >
                                {ns}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Pod
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      value={podQuery}
                      onChange={(e) => setPodQuery(e.target.value)}
                      placeholder="Search pods..."
                      disabled={!selectedNamespace}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-60"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={startStream}
                  disabled={!selectedNamespace || !selectedPod}
                  className="flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  Stream logs
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={downloadLogs}
                  disabled={!selectedNamespace || !selectedPod}
                  className="flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download logs
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openEvents}
                  disabled={!selectedNamespace || !selectedPod}
                  className="flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  View events
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowRestartConfirm(true)}
                  disabled={!selectedNamespace || !selectedPod || isRestartingPod}
                  className="flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  {isRestartingPod ? 'Restarting...' : 'Restart pod'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openPodTerminal}
                  disabled={!selectedNamespace || !selectedPod}
                  className="flex items-center gap-2"
                >
                  <TerminalSquare className="w-4 h-4" />
                  Terminal
                </Button>
              </div>
              {showRestartConfirm && selectedPod && (
                <InlineConfirmation
                  message={`Delete pod ${selectedPod}? The controller will recreate it.`}
                  onConfirm={restartPod}
                  onCancel={() => setShowRestartConfirm(false)}
                  confirmText="Restart"
                  cancelText="Cancel"
                  variant="danger"
                  isLoading={isRestartingPod}
                  loadingText="Restarting..."
                />
              )}
            </div>

            {/* Pod list */}
            {loading ? (
              <div className="space-y-2 py-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : !selectedNamespace ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                Select a namespace to list pods.
              </div>
            ) : filteredPods.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                {podQuery ? `No pods matching "${podQuery}"` : 'No pods found in this namespace'}
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                          Pod
                          {podQuery && (
                            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                              ({filteredPods.length} of {pods.length})
                            </span>
                          )}
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Phase</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Ready</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Restarts</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredPods.map((p) => (
                        <tr
                          key={p.name}
                          className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer ${selectedPod === p.name ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                          onClick={() => setSelectedPod(p.name)}
                        >
                          <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-gray-100">{p.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {p.phase}{p.reason ? ` (${p.reason})` : ''}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{p.ready}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{p.restarts}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden space-y-2">
                  {filteredPods.map((p) => (
                    <div
                      key={p.name}
                      className={`border border-gray-200 dark:border-gray-700 rounded-lg p-3 cursor-pointer ${selectedPod === p.name ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''}`}
                      onClick={() => setSelectedPod(p.name)}
                    >
                      <div className="font-mono text-sm break-all text-gray-900 dark:text-gray-100">{p.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {p.phase}{p.reason ? ` (${p.reason})` : ''} • Ready {p.ready} • Restarts {p.restarts}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>

      {/* Logs modal */}
      <Dialog open={isLogDialogOpen} onOpenChange={(o) => setIsLogDialogOpen(o)}>
        <DialogContent maxWidth="4xl" className="flex flex-col h-[90vh] overflow-hidden w-full md:w-[80vw] md:max-w-[80vw]">
          <DialogHeader>
            <DialogTitle>
              Pod Logs: {instanceName} / {selectedNamespace} / {selectedPod || ''}
            </DialogTitle>
            <DialogDescription>Live stream (SSE)</DialogDescription>
          </DialogHeader>

          <DialogBody className="flex-1 p-0 min-h-0 overflow-hidden">
            <div className="flex flex-col min-h-0 h-full">
              <LogSearchBar
                query={logSearchQuery}
                setQuery={setLogSearchQuery}
                before={logCtxBefore}
                setBefore={setLogCtxBefore}
                after={logCtxAfter}
                setAfter={setLogCtxAfter}
                matchCount={filteredLogs.matchCount}
                totalLines={logLines.length}
              />
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => setLogLines([])}>
                    Clear
                  </Button>
                  <Button size="sm" variant="outline" onClick={downloadLogs} disabled={!selectedPod}>
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
                {isStreaming ? (
                  <Button size="sm" className="bg-red-600 hover:bg-red-700 flex items-center gap-2" onClick={stopStream}>
                    <Square className="w-4 h-4" />
                    Stop
                  </Button>
                ) : (
                  <Button size="sm" className="flex items-center gap-2" onClick={startStream} disabled={!selectedPod}>
                    <Play className="w-4 h-4" />
                    Stream
                  </Button>
                )}
              </div>

              <div className="flex-1 min-h-0 relative">
                <div
                  ref={logContainerRef}
                  className="h-full overflow-y-scroll p-4 bg-gray-900 text-green-400 font-mono text-sm scrollbar-hidden"
                >
                  {filteredLogs.lines.length === 0 && !isStreaming ? (
                    <div className="text-gray-500">{hasLogSearch ? 'No matches' : 'Click "Stream" to start viewing logs'}</div>
                  ) : (
                    <>
                      {filteredLogs.lines.map((l) => (
                        <div
                          key={`log-${l.idx}`}
                          className={l.isMatch ? 'bg-yellow-500/10 ring-1 ring-yellow-500/30 rounded px-1' : (hasLogSearch ? 'opacity-80' : '')}
                        >
                          <span className="whitespace-pre-wrap break-all">
                            {highlightMatches(l.text, logSearchQuery).map((p, i) =>
                              p.isHit ? (
                                <span key={i} className="bg-yellow-500/40 text-yellow-50 px-0.5 rounded">
                                  {p.text}
                                </span>
                              ) : (
                                <span key={i}>{p.text}</span>
                              ),
                            )}
                          </span>
                        </div>
                      ))}
                      {isStreaming && (
                        <div className="text-yellow-400 animate-pulse">Streaming...</div>
                      )}
                    </>
                  )}
                </div>

                {isStreaming && !hasLogSearch && !isLogPinnedToBottom && (
                  <div className="absolute right-3 bottom-3">
                    <Button
                      size="sm"
                      onClick={() => {
                        const el = logContainerRef.current;
                        if (!el) return;
                        el.scrollTop = el.scrollHeight;
                        pinnedRef.current = true;
                        setIsLogPinnedToBottom(true);
                      }}
                    >
                      Jump to bottom
                    </Button>
                  </div>
                )}

                {/* Thick, always-visible, draggable scrollbar overlay */}
                {logScrollThumb.visible && (
                  <div
                    className="absolute right-2 top-2 bottom-2 w-3"
                    onPointerDown={(e) => {
                      const el = logContainerRef.current;
                      if (!el) return;
                      isDraggingThumbRef.current = true;
                      lastPointerIdRef.current = e.pointerId;
                      (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
                      e.preventDefault();
                    }}
                    onPointerMove={(e) => {
                      if (!isDraggingThumbRef.current) return;
                      const el = logContainerRef.current;
                      if (!el) return;

                      const track = e.currentTarget as HTMLDivElement;
                      const rect = track.getBoundingClientRect();
                      const y = Math.min(Math.max(e.clientY - rect.top, 0), rect.height);
                      const thumbHeightPx = (logScrollThumb.heightPct / 100) * rect.height;
                      const maxThumbTopPx = Math.max(1, rect.height - thumbHeightPx);
                      const thumbTopPx = Math.min(Math.max(y - thumbHeightPx / 2, 0), maxThumbTopPx);
                      const thumbTopPct = maxThumbTopPx > 0 ? thumbTopPx / maxThumbTopPx : 0;

                      const maxScrollTop = el.scrollHeight - el.clientHeight;
                      el.scrollTop = thumbTopPct * maxScrollTop;
                    }}
                    onPointerUp={(e) => {
                      if (lastPointerIdRef.current !== e.pointerId) return;
                      isDraggingThumbRef.current = false;
                      lastPointerIdRef.current = null;
                    }}
                    onPointerCancel={(e) => {
                      if (lastPointerIdRef.current !== e.pointerId) return;
                      isDraggingThumbRef.current = false;
                      lastPointerIdRef.current = null;
                    }}
                  >
                    <div className="absolute inset-0 rounded-full bg-black/25" />
                    <div
                      className="absolute left-0 right-0 rounded-full bg-gray-400/70"
                      style={{ top: `${logScrollThumb.topPct}%`, height: `${logScrollThumb.heightPct}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>

      {/* Events modal */}
      <Dialog open={isEventsOpen} onOpenChange={(o) => setIsEventsOpen(o)}>
        <DialogContent maxWidth="4xl" className="flex flex-col max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Pod Events: {instanceName} / {selectedNamespace} / {selectedPod || ''}</DialogTitle>
            <DialogDescription>Events for the selected pod</DialogDescription>
          </DialogHeader>

          <DialogBody>
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={eventsQuery}
                  onChange={(e) => setEventsQuery(e.target.value)}
                  placeholder="Search events..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>

            {eventsLoading ? (
              <div className="flex items-center justify-center py-10 text-gray-500 dark:text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Loading events...
              </div>
            ) : filteredEvents.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                {eventsQuery ? `No events matching "${eventsQuery}"` : 'No events found for this pod'}
              </div>
            ) : (
              <div className="overflow-y-auto max-h-[60vh] border border-gray-200 dark:border-gray-700 rounded-lg">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Time</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Reason</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">Message</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredEvents.map((e, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400 font-mono">
                          {e.lastTimestamp || e.firstTimestamp || ''}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{e.type || ''}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{e.reason || ''}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 break-all">
                          {e.message || ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>

      <TerminalSessionDialog
        open={termOpen}
        onClose={() => {
          setTermOpen(false);
          setTermPath(null);
        }}
        title={`Terminal: ${instanceName} / ${selectedNamespace} / ${selectedPod || ''}`}
        description={execContainer ? `Container ${execContainer}` : 'Interactive pod shell'}
        pathWithQuery={termPath}
      />
    </>
  );
}

